/**
 * `progress` — `ProgressValue` (`specs/M2-tier2-column-types.md` §5.15,
 * `[09§A.7 progress]`, `[cvs§2 progress]`).
 *
 * *"a calculated column — its value is derived from the "done" state of related status columns.
 * You cannot directly update or clear its value."* monday returns `value: null` and `text: ""`
 * on **every** row (59/59 sampled) and `ProgressValue` declares no extra field, so there is
 * nothing to import and nothing to serve: the number only exists in the UI.
 *
 * We reproduce that exactly — `api: { value: 'null', text: 'empty' }` — and compute the
 * percentage locally for the renderer and the group summary from
 * `settings.related_columns.columns`, the weighted set of status columns monday stores:
 *
 * ```json
 * {"related_columns": {"isNormalized": true,
 *                      "columns": {"status": {"isSelected": true, "percentage": 100}}}}
 * ```
 *
 * A status column contributes its weight when the cell's label is a **done** label
 * (`settings.done_colors` of *that* column), which is the same "done" the battery summary uses.
 */
import { defaultFilterSql } from '../sql.js';
import { z } from 'zod';
import { asColumnId } from '../../ids.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const progressValueSchema = z.looseObject({ percentage: z.number().min(0).max(100) });
export const progressRelatedColumnSchema = z.looseObject({
    isSelected: z.boolean().optional(),
    percentage: z.number().optional(),
});
import { progressFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const progressSettingsSchema = z.looseObject({
    related_columns: z
        .looseObject({
        /** `true` = the weights are rescaled to add up to 100 `[cvs§2 progress]`. */
        isNormalized: z.boolean().optional(),
        columns: z.record(z.string(), progressRelatedColumnSchema).optional(),
    })
        .optional(),
    hide_footer: z.boolean().optional(),
});
/** The status columns this progress column tracks, with their weights, in settings order. */
export function progressSources(settings) {
    const columns = settings.related_columns?.columns ?? {};
    return Object.entries(columns)
        .filter(([, entry]) => entry.isSelected !== false)
        .map(([columnId, entry]) => ({ columnId, weight: entry.percentage ?? 0 }));
}
/**
 * The percentage a row shows. `doneOf(columnId)` answers "is that status cell a done label" —
 * the caller owns it because only the *status* column's own settings know which ids are done
 * (CLAUDE.md rule 6).
 */
export function progressPercentage(settings, doneOf) {
    const sources = progressSources(settings);
    if (sources.length === 0)
        return null;
    const total = sources.reduce((sum, source) => sum + source.weight, 0);
    if (total <= 0)
        return null;
    const done = sources.reduce((sum, source) => sum + (doneOf(source.columnId) === true ? source.weight : 0), 0);
    return Math.round((done / total) * 100);
}
export const progressColumn = defineColumnType({
    type: 'progress',
    title: 'Progress Tracking',
    description: 'The share of the tracked status columns that are done. Read-only.',
    apiFragmentName: 'ProgressValue',
    legacyTypeName: 'columns-battery',
    idPrefix: 'progress',
    valueSchema: progressValueSchema,
    settingsSchema: progressSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: progressSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    filterConditions: progressFilterConditions,
    filterOperators: operatorsOf(progressFilterConditions),
    /**
     * No footer aggregation: the percentage is never materialised (`materialise: 'never'`), so
     * there is no shadow for SQL to average. The renderer draws the per-row bar (spec §5.15).
     */
    aggregations: ['none'],
    /** monday: `text` is `""` on every row, never null `[cvs§2 progress]`. */
    emptyText: '',
    computed: {
        dependsOn(column, settings) {
            const parsed = progressSettingsSchema.parse(settings ?? {});
            return progressSources(parsed).map((source) => ({
                kind: 'column',
                boardId: column.boardId,
                columnId: asColumnId(source.columnId),
            }));
        },
        compute() {
            /*
             * Not computed here: the percentage needs each source column's *settings* to know which
             * labels are done, and a `ComputeContext` carries sibling **values** only. The read path
             * calls `progressPercentage()` with a `doneOf` it can answer; this hook exists so the
             * dependency rows are materialised and the worker knows the cell went stale.
             */
            return null;
        },
        materialise: 'never',
        /** monday exposes neither: `value: null`, `text: ""` `[cvs§2 progress]`. */
        api: { value: 'null', text: 'empty' },
    },
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        throw new ColumnValueError('progress', 'the progress column is calculated and cannot be written');
    },
    toText() {
        return '';
    },
    toSortKeys(value) {
        return { text: null, number: value?.percentage ?? null, date: null };
    },
    isEqual(a, b) {
        return (a?.percentage ?? null) === (b?.percentage ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { percentage: value.percentage };
    },
    fromMondayValue() {
        // Derived: *"Recompute, never import"* `[cvs§4.6]`.
        return null;
    },
    toMondayValue() {
        return null;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return progressSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment() {
        // `ProgressValue` exposes no extra field `[cvs§2 progress]`.
        return {};
    },
    toWebhookValue() {
        return null;
    },
    /**
     * **Measured correction (round 5o).** monday offers exactly `is` / `is not` on a progress
     * column, over its computed percentage — the `number_value` shadow.
     */
    filterSql(ctx) {
        return defaultFilterSql('number', ctx);
    },
    summarySql() {
        return null;
    },
});
//# sourceMappingURL=index.js.map